import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  AcademicCapIcon,
  HeartIcon,
  GlobeAltIcon,
  CheckBadgeIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

const OurSchool = () => {
  return (
    <main id="main-content" className="bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 pb-24 bg-[#4a0402] overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block bg-white text-[#630503] px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              About P.S. 11X
            </span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              Our School
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Two Buildings, One School, One Family - Discover the heart of P.S. 11X The Highbridge School
            </p>
          </div>
        </div>
      </section>

      {/* Administrative Team Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Leadership
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Meet Our Administrative Team
            </h2>
          </div>

          {/* Principal's Welcome */}
          <div className="mb-20">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="bg-gray-200 h-80 lg:h-auto relative overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                    alt="Principal Daisy Joy Rodrigo" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>
                <div className="p-12 flex flex-col justify-center bg-gray-50">
                  <div className="mb-6">
                    <span className="inline-block bg-[#630503] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider rounded-lg shadow-md">
                      Leadership
                    </span>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">Principal's Welcome</h3>
                  <p className="text-gray-600 leading-relaxed mb-6 text-lg">
                    My name is Daisy Joy Rodrigo and I am humbled and honored to serve the Highbridge community in this role. 
                    I believe in the brilliance that exists in the Bronx, as I was once part of this community. It has always been a 
                    dream of mine to return to the community that welcomed my immigrant parents as they started out life in 
                    America. I am back home in District 9!
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-8 text-lg">
                    I look forward to meeting you all, and getting to know the community that makes up P.S. 11.
                  </p>
                  <div className="border-l-4 border-[#630503] pl-6">
                    <div className="text-[#630503] font-bold text-lg">Daisy Joy Rodrigo</div>
                    <div className="text-gray-500 text-sm uppercase tracking-wide">Principal</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Assistant Principals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Henrietta Sirleaf */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-gray-200 h-64">
                <img 
                  src="https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Henrietta Sirleaf" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-10">
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Henrietta Sirleaf</h4>
                <p className="text-[#630503] font-semibold mb-6 text-sm uppercase tracking-wide">Assistant Principal</p>
                <p className="text-gray-600 leading-relaxed mb-6 text-lg">
                  Welcome to the P.S 11 Family. I am Ms. Sirleaf and as a lifelong educator, I believe that every day is an 
                  opportunity to learn something new, and our students can accomplish anything they set their minds to.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg">
                  I have a strong desire to make a positive impact on their society.
                </p>
              </div>
            </div>

            {/* Alejandra Urteaga */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-gray-200 h-64">
                <img 
                  src="https://images.unsplash.com/photo-1607990281513-2c110a25bd8c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Alejandra Urteaga" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="p-10">
                <h4 className="text-2xl font-bold text-gray-900 mb-3">Alejandra Urteaga</h4>
                <p className="text-[#630503] font-semibold mb-6 text-sm uppercase tracking-wide">Assistant Principal</p>
                <p className="text-gray-600 leading-relaxed mb-6 text-lg">
                  My name is Alejandra Urteaga. I am a wife, mother, immigrant and educator. I am honored to be working at PS 11 
                  as an Assistant Principal.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg">
                  I am a believer that all children should have the opportunity and equity to make great things happen!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Our Foundation
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Mission & Vision
            </h2>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Mission */}
            <div className="bg-white rounded-xl p-12 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="flex items-center mb-8">
                <div className="bg-[#630503] p-4 rounded-xl mr-6 shadow-md">
                  <CheckBadgeIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Our Mission</h3>
              </div>
              <p className="text-gray-600 leading-relaxed text-lg">
                PS 011X, in partnership with the entire school community, prepares every student for lifelong 
                success through excellence in education.
              </p>
              <div className="mt-8 w-16 h-1 bg-gradient-to-r from-[#630503] to-gray-200 rounded-full"></div>
            </div>

            {/* Vision */}
            <div className="bg-white rounded-xl p-12 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="flex items-center mb-8">
                <div className="bg-[#630503] p-4 rounded-xl mr-6 shadow-md">
                  <EyeIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900">Our Vision</h3>
              </div>
              <p className="text-gray-600 leading-relaxed text-lg mb-6">
                We envision a school with equitable opportunities, which honors culture, linguistics, 
                race, gender, and the many identities of our students, families and staff.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg">
                We strive to contribute towards equitable teaching and learning practices and culturally 
                responsive experiences to ensure our children experience a sense of belonging, love and joy.
              </p>
              <div className="mt-8 w-16 h-1 bg-gradient-to-r from-[#630503] to-gray-200 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs & Partnerships Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Excellence in Education
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Our Programs and Partnerships
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We collaborate with leading organizations to provide exceptional educational opportunities for our students.
            </p>
          </div>

          {/* Programs Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16">
            {/* Dual Language Program */}
            <div className="bg-blue-50 rounded-xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-blue-600 w-16 h-16 rounded-xl flex items-center justify-center mb-8 shadow-md">
                <GlobeAltIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">Dual Language Program</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Bilingual education program fostering academic excellence in both English and Spanish.
              </p>
            </div>

            {/* No Place for Hate */}
            <div className="bg-yellow-50 rounded-xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-yellow-600 w-16 h-16 rounded-xl flex items-center justify-center mb-8 shadow-md">
                <HeartIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">No Place for Hate</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Anti-Defamation League program promoting respect, inclusion, and anti-bias education.
              </p>
            </div>

            {/* CookShop Classroom */}
            <div className="bg-green-50 rounded-xl p-10 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <div className="bg-green-600 w-16 h-16 rounded-xl flex items-center justify-center mb-8 shadow-md">
                <AcademicCapIcon className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-5">CookShop Classroom</h3>
              <p className="text-gray-600 leading-relaxed text-lg">
                Hands-on nutrition education program connecting food, health, and academic learning.
              </p>
            </div>
          </div>

          {/* Partnership Logos */}
          <div className="bg-gray-50 rounded-xl p-12 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">Our Valued Partners</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
              {/* United Federation of Teachers */}
              <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300 flex items-center justify-center h-32">
                <div className="text-center">
                  <div className="text-blue-600 font-bold text-lg mb-2">UFT</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Teacher Center</div>
                </div>
              </div>

              {/* NYC Public Schools */}
              <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300 flex items-center justify-center h-32">
                <div className="text-center">
                  <div className="text-blue-800 font-bold text-lg mb-2">NYC</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Public Schools</div>
                </div>
              </div>

              {/* SEP Program */}
              <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300 flex items-center justify-center h-32">
                <div className="text-center">
                  <div className="text-teal-700 font-bold text-lg mb-2">SEP</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Software Engineering</div>
                </div>
              </div>

              {/* Computer Science for All */}
              <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300 flex items-center justify-center h-32">
                <div className="text-center">
                  <div className="text-orange-700 font-bold text-lg mb-2">CS4ALL</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Computer Science</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* School Information Section */}
      <section className="py-24 bg-[#630503]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-8 leading-tight">
                Two Buildings,<br />
                One School,<br />
                One Family
              </h2>
              <p className="text-white/90 text-xl leading-relaxed mb-10">
                PS 011X Highbridge Elementary serves our community with pride, bringing together students, 
                families, and educators in a shared commitment to excellence.
              </p>
              <div className="bg-white/10 rounded-xl p-10 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-white mb-6">School Address</h3>
                <p className="text-white/90 text-lg leading-relaxed">
                  1257 Ogden Avenue<br />
                  Bronx, NY 10452
                </p>
              </div>
            </div>
            
            <div className="bg-white/10 rounded-xl p-12 backdrop-blur-sm">
              <h3 className="text-2xl font-bold text-white mb-10 text-center">Our School by the Numbers</h3>
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <div className="text-5xl font-bold mb-4 text-white">1889</div>
                  <div className="text-sm uppercase tracking-wider font-semibold text-white/90">Established</div>
                  <div className="w-12 h-1 bg-white/30 mx-auto mt-3 rounded-full"></div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold mb-4 text-white">450+</div>
                  <div className="text-sm uppercase tracking-wider font-semibold text-white/90">Students</div>
                  <div className="w-12 h-1 bg-white/30 mx-auto mt-3 rounded-full"></div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold mb-4 text-white">25+</div>
                  <div className="text-sm uppercase tracking-wider font-semibold text-white/90">Teachers</div>
                  <div className="w-12 h-1 bg-white/30 mx-auto mt-3 rounded-full"></div>
                </div>
                <div className="text-center">
                  <div className="text-5xl font-bold mb-4 text-white">Pre-K-5</div>
                  <div className="text-sm uppercase tracking-wider font-semibold text-white/90">Grades</div>
                  <div className="w-12 h-1 bg-white/30 mx-auto mt-3 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default OurSchool; 